import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Award, Briefcase, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Story({
  isCompact = false
}: {
  isCompact?: boolean;
}) {
  return <div className="flex flex-col w-full" data-id="element-401">
      {/* Founder Section */}
      <section id="story" className="py-16 md:py-20 bg-white overflow-hidden" data-id="element-402">
        <div className="max-w-7xl mx-auto px-6" data-id="element-403">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-404">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-405">
              <div className="aspect-[4/5] relative z-10" data-id="element-406">
                <img src="https://cdn.magicpatterns.com/uploads/7Ne571hqNffS95gnvgj9gH/Chef_Photoshoot(7).jpg" alt="Tisha Djissenou - Founder" className="w-full h-full object-cover rounded-sm grayscale hover:grayscale-0 transition-all duration-700" data-id="element-407" />
              </div>
              {/* Decorative Gold Frame */}
              <div className="absolute -top-6 -left-6 w-full h-full border-2 border-brand-gold/30 -z-0" data-id="element-408" />
              <div className="absolute -bottom-4 -right-4 bg-brand-gold p-8 z-20 hidden lg:block" data-id="element-409">
                <p className="text-white font-serif text-xl italic" data-id="element-410">
                  "Heritage is the secret ingredient in every dish we serve."
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-411">
              <h2 className="text-4xl md:text-5xl mb-8 font-serif" data-id="element-412">
                A Fusion of Heritage
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-413">
                <p data-id="element-414">
                  Founded by{' '}
                  <span className="text-brand-black font-bold" data-id="element-415">
                    Tisha Djissenou
                  </span>
                  , Versatile LLC is more than a catering company—it's a
                  celebration of cultural inclusivity and culinary innovation.
                </p>
                {!isCompact ? <>
                    <p data-id="element-416">
                      Our mission is rooted in women's empowerment and the rich
                      tapestry of African heritage. We blend traditional flavors
                      with modern techniques to create dining experiences that
                      resonate with the diverse DMV community.
                    </p>
                    <p data-id="element-417">
                      Whether it's a grand wedding or an intimate corporate
                      gathering, we bring a level of craft and care that only a
                      woman-owned, heritage-led business can provide.
                    </p>
                  </> : <div className="mt-8" data-id="element-418">
                    <Link to="/story" className="inline-flex items-center gap-2 text-brand-gold font-bold uppercase tracking-widest text-sm hover:text-brand-black transition-colors" data-id="element-419">
                      Read Our Full Story &rarr;
                    </Link>
                  </div>}
              </div>
              <div className="mt-10 pt-10 border-t border-gray-100" data-id="element-420">
                <div className="flex items-center gap-4" data-id="element-421">
                  <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center text-brand-gold font-serif text-2xl" data-id="element-422">
                    T
                  </div>
                  <div data-id="element-423">
                    <p className="font-bold text-brand-black" data-id="element-424">
                      Tisha Djissenou
                    </p>
                    <p className="text-sm text-gray-500 uppercase tracking-widest" data-id="element-425">
                      Founder & Executive Chef
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {!isCompact && <>
          {/* Executive Summary Section */}
          <section className="py-16 md:py-20 bg-brand-black text-white relative overflow-hidden" data-id="element-426">
            <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center mix-blend-overlay" data-id="element-427" />
            <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-428">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-429">
                <h2 className="text-3xl md:text-5xl font-serif mb-8 text-brand-gold" data-id="element-430">
                  Our Vision
                </h2>
                <p className="text-lg md:text-2xl leading-relaxed text-white/90 font-serif italic" data-id="element-431">
                  "Versatile LLC is an African-woman-owned catering business
                  dedicated to delivering high-quality, diverse, and customized
                  culinary experiences."
                </p>
                <div className="w-24 h-[1px] bg-brand-gold mx-auto my-8" data-id="element-432" />
                <p className="text-base md:text-lg text-gray-300 leading-relaxed font-sans" data-id="element-433">
                  Our mission is to provide exceptional food and service that
                  cater to various cultural backgrounds, dietary needs, and
                  occasions. By focusing on inclusivity, innovation, and
                  outstanding customer service, we aim to prove ourselves as a
                  premier catering partner for corporate events, weddings,
                  social gatherings, community functions, and meal plans.
                </p>
              </motion.div>
            </div>
          </section>
        </>}

      {/* Business Description / At a Glance */}
      <section className="py-16 md:py-20 bg-gray-50" data-id="element-434">
        <div className="max-w-7xl mx-auto px-6" data-id="element-435">
          <div className="text-center mb-12" data-id="element-436">
            <h2 className="text-3xl md:text-5xl font-serif mb-4 text-brand-black" data-id="element-437">
              Versatile LLC At a Glance
            </h2>
            <p className="text-gray-500 text-lg" data-id="element-438">
              The core pillars of our culinary identity and service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-439">
            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-440">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-441">
                <Heart size={24} data-id="element-442" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-443">Ownership</h3>
              <p className="text-gray-600" data-id="element-444">
                Proudly{' '}
                <span className="font-bold text-brand-black" data-id="element-445">
                  African-Woman-Owned
                </span>
                , bringing authentic heritage and female empowerment to the
                forefront of the culinary industry.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-446">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-447">
                <MapPin size={24} data-id="element-448" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-449">Location</h3>
              <p className="text-gray-600" data-id="element-450">
                Based in{' '}
                <span className="font-bold text-brand-black" data-id="element-451">
                  Gaithersburg, MD
                </span>
                , strategically positioned to serve the entire Washington D.C.,
                Maryland, and Northern Virginia (DMV) area.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-452">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-453">
                <Briefcase size={24} data-id="element-454" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-455">What We Do</h3>
              <p className="text-gray-600" data-id="element-456">
                We offer comprehensive{' '}
                <span className="font-bold text-brand-black" data-id="element-457">
                  Catering Services
                </span>
                , from full-scale luxury event production to convenient drop-off
                and personalized meal prep solutions.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100 lg:col-span-2" data-id="element-458">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-459">
                <Users size={24} data-id="element-460" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-461">
                Who We Serve
              </h3>
              <p className="text-gray-600" data-id="element-462">
                We craft unforgettable culinary experiences for corporate
                events, weddings, private parties, and social gatherings. We
                also offer tailored meal plans for busy professionals who want
                healthy, gourmet meals without the prep time.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-brand-black text-white p-8 rounded-sm shadow-xl border border-brand-gold/20 lg:col-span-1" data-id="element-463">
              <div className="w-12 h-12 bg-brand-gold text-white rounded-full flex items-center justify-center mb-6" data-id="element-464">
                <Award size={24} data-id="element-465" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3 text-brand-gold" data-id="element-466">
                What Makes Us Different
              </h3>
              <p className="text-gray-300" data-id="element-467">
                We bring a unique fusion of diverse cuisines, fully customized
                menus, and a deep commitment to cultural inclusivity—all proudly
                driven by female empowerment in the culinary arts.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}